import { useAuth } from '@clerk/clerk-react'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { AuthNotice } from '@/components/auth/AuthNotice'
import { DigestPreferences } from '@/components/stash/DigestPreferences'
import {
  StashEmptyLinks,
  StashPointsPanel,
} from '@/components/stash/StashDashboard'
import { StashCard } from '@/components/stash/StashCard'
import { Button } from '@/components/ui/Button'
import { useMe } from '@/context/MeProvider'
import { useAuthRedirect } from '@/hooks/useAuthRedirect'
import { ApiError } from '@/lib/api/client'
import { listMyListings } from '@/lib/api/me'
import type { OwnerListing } from '@/lib/types/listing'

export function StashPage() {
  const { isSignedIn, getToken } = useAuth()
  const { me, loading: meLoading } = useMe()
  const { goToSignIn } = useAuthRedirect()
  const location = useLocation()
  const checkHeld =
    typeof location.state === 'object' &&
    location.state !== null &&
    'checkHeld' in location.state &&
    location.state.checkHeld === true
  const [listings, setListings] = useState<OwnerListing[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isSignedIn) {
      setListings([])
      setLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    listMyListings(getToken)
      .then((data) => {
        if (!cancelled) {
          setListings(data)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.detail : "Couldn't load your stash.",
          )
          setListings([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isSignedIn, getToken])

  function replaceListing(next: OwnerListing) {
    setListings((current) =>
      current.map((item) => (item.id === next.id ? next : item)),
    )
  }

  return (
    <main
      id="main"
      className="max-w-site px-margin-mobile md:px-margin-desktop mx-auto flex w-full flex-grow flex-col gap-8 pt-24 pb-24"
    >
      {!isSignedIn && (
        <section className="space-y-4">
          <h1 className="text-headline-lg-mobile text-fg md:text-headline-lg">
            Your stash
          </h1>
          <p className="font-body text-body-md text-fg-muted">
            Sign in to see your points and manage the links you&apos;ve posted.
          </p>
          <Button
            type="button"
            variant="gradient"
            size="md"
            onClick={goToSignIn}
          >
            Sign in
          </Button>
        </section>
      )}

      {isSignedIn && (
        <>
          {checkHeld && (
            <div
              role="status"
              className="border-primary-container/30 bg-primary-container/15 rounded-lg border px-4 py-3"
            >
              <p className="font-body text-body-md text-fg font-semibold">
                We held this one for a closer look.
              </p>
              <p className="font-body text-body-sm text-fg mt-1">
                It&apos;s in your stash while we review the link. That keeps
                Discover clean for everyone else.
              </p>
            </div>
          )}
          {meLoading && me === null && (
            <section
              aria-busy="true"
              className="border-glass-border bg-surface-container-high/50 h-40 animate-pulse rounded-xl border"
            />
          )}
          {me !== null && (
            <StashPointsPanel me={me} linkCount={listings.length} />
          )}
          {me !== null && <DigestPreferences me={me} />}

          <AuthNotice action="manage" />

          <section
            className="flex flex-col gap-6"
            aria-labelledby="my-links-heading"
          >
            <h2
              id="my-links-heading"
              className="font-display text-headline-lg-mobile text-fg flex items-center gap-2"
            >
              <span
                aria-hidden="true"
                className="bg-primary-container inline-block h-6 w-1 rounded-full"
              />
              Your links
            </h2>

            {loading && (
              <p className="font-body text-body-md text-fg-muted">
                Loading your links…
              </p>
            )}
            {error && (
              <p className="font-body text-body-md text-error" role="alert">
                {error}
              </p>
            )}
            {!loading && !error && listings.length === 0 && <StashEmptyLinks />}
            {!loading && listings.length > 0 && (
              <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {listings.map((listing) => (
                  <li key={listing.id}>
                    <StashCard listing={listing} onChange={replaceListing} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </main>
  )
}
